import React, { useCallback } from 'react';
import { adminApiService } from '../../services/adminApi';
import EntityFilter, { FILTER_PAGE_SIZE, FilterOption, FilterOptionPage } from '../filters/EntityFilter';
import { apiKeyLabel } from '../../utils/apiKey';

// Table cell styles, sortable header, and empty row live in TableKit;
// StatTile lives in cards/StatCards. Re-exported so admin components keep
// importing everything table/card-shared from here.
export { thClass, tdClass, SortableTh, EmptyRow } from '../table/TableKit';
export { StatTile } from '../cards/StatCards';
export { ErrorNotice } from '../ErrorNotice';
export { Badge } from '../StatusBadge';
export { formatSeconds as formatDuration } from '../../utils/dateTime';

// Shared styles for admin console filters. Compact (py-1.5, text-sm) so
// filters sit level with the table-header search and pagination.
// Fixed h-[34px] matches the search/pager height; without it, date
// inputs render taller than selects.
export const inputClass =
  'h-[34px] bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md px-3 py-1.5 text-sm';
export const selectClass = `${inputClass} select-chevron pl-3 pr-8`;

export function formatNumber(value: number | null | undefined): string {
  return value == null ? '—' : value.toLocaleString();
}

export function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center h-40">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
    </div>
  );
}

export { useDebounced } from '../../hooks/useDebounced';

export function instanceLabel(name: string | null, key: string | null): string {
  if (name && key && name !== key) return `${name} (${key})`;
  return name || key || '—';
}

// Searchable, paged instance filter. Single choice: the admin endpoints scope
// by one instance_id.
export function InstanceSelect({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}) {
  const loadPage = useCallback(async (search: string, offset: number): Promise<FilterOptionPage> => {
    const page = await adminApiService.getInstances({
      search: search || undefined,
      limit: FILTER_PAGE_SIZE,
      offset,
      sort: 'last_activity_at',
      order: 'desc',
    });
    return {
      options: page.items.map((inst) => ({
        id: inst.id,
        label: inst.name || inst.instance_key,
        sublabel: inst.name && inst.name !== inst.instance_key ? inst.instance_key : undefined,
      })),
      total: page.total,
    };
  }, []);

  const loadSelected = useCallback(async (ids: number[]): Promise<FilterOption[]> => {
    const page = await adminApiService.getInstances({
      ids: ids.join(','),
      limit: ids.length,
      offset: 0,
    });
    return page.items.map((inst) => ({
      id: inst.id,
      label: inst.name || inst.instance_key,
      sublabel: inst.name && inst.name !== inst.instance_key ? inst.instance_key : undefined,
    }));
  }, []);

  return (
    <EntityFilter
      ariaLabel="Instance filter"
      allLabel="All Instances"
      pluralNoun="instances"
      value={value ? [value] : []}
      onChange={(ids) => onChange(ids[0])}
      loadPage={loadPage}
      loadSelected={loadSelected}
    />
  );
}

// API key filter across instances. Scoped to one instance when the instance
// filter is set, so the two filters can never contradict each other.
export function ApiKeySelect({
  instanceId,
  scope,
  value,
  onChange,
}: {
  instanceId: number | undefined;
  scope: string;
  value: number[];
  onChange: (ids: number[]) => void;
}) {
  const loadPage = useCallback(
    async (search: string, offset: number): Promise<FilterOptionPage> => {
      const page = await adminApiService.getApiKeys({
        instance_id: instanceId,
        scope,
        search: search || undefined,
        limit: FILTER_PAGE_SIZE,
        offset,
        sort: 'name',
        order: 'asc',
      });
      return {
        options: page.items.map((key) => ({
          id: key.id,
          label: apiKeyLabel(key),
          sublabel: instanceLabel(key.instance_name, key.instance_key),
          badge: key.is_active ? undefined : 'revoked',
        })),
        total: page.total,
      };
    },
    [instanceId, scope],
  );

  const loadSelected = useCallback(
    async (ids: number[]): Promise<FilterOption[]> => {
      const page = await adminApiService.getApiKeys({
        ids: ids.join(','),
        instance_id: instanceId,
        scope,
        limit: ids.length,
        offset: 0,
      });
      return page.items.map((key) => ({
        id: key.id,
        label: apiKeyLabel(key),
        sublabel: instanceLabel(key.instance_name, key.instance_key),
        badge: key.is_active ? undefined : 'revoked',
      }));
    },
    [instanceId, scope],
  );

  return (
    <EntityFilter
      multiple
      ariaLabel="API key filter"
      allLabel="All API Keys"
      pluralNoun="API keys"
      value={value}
      onChange={onChange}
      loadPage={loadPage}
      loadSelected={loadSelected}
      resetKey={`${instanceId ?? ''}:${scope}`}
    />
  );
}
